import { Clock, Mail, MapPin, Package, Phone, Play, Search } from "lucide-react";
import { CONTACT_INFO } from "../data";
import { formatPrice } from "../format";
import { useCatalogI18n } from "../i18n";
import { BG, BLUE, BORDER, FONT, GOLD, MUTED, WHITE } from "../theme";
import type { Dessert, DessertSet, Partner } from "../types";
import { PartnerMap, type PartnerMapMarker } from "./PartnerMap";

// Keyless Yandex map-widget embed. Coordinates are [longitude, latitude];
// `ll` centers the map and `pt` drops a placemark at the same spot.
function yandexMapSrc([lng, lat]: [number, number]): string {
  const point = `${lng},${lat}`;
  return `https://yandex.ru/map-widget/v1/?ll=${point}&z=16&pt=${point},pm2rdm`;
}

type HomePageProps = {
  loading: boolean;
  categories: string[];
  selectedCategory: string;
  searchQuery: string;
  filteredDesserts: Dessert[];
  onSearchQueryChange: (value: string) => void;
  onSelectCategory: (category: string) => void;
  onOpenDetail: (dessert: Dessert) => void;
  onAddToCart: (dessert: Dessert) => void;
};

export function HomePage({
  loading,
  categories,
  selectedCategory,
  searchQuery,
  filteredDesserts,
  onSearchQueryChange,
  onSelectCategory,
  onOpenDetail,
  onAddToCart
}: HomePageProps) {
  const { copy, localizeCategory } = useCatalogI18n();
  const hasTitle = Boolean(copy.home.titleLead || copy.home.titleEmphasis);
  const hasHero = Boolean(copy.home.badge || hasTitle || copy.home.description);

  return (
    <>
      {hasHero && (
        <section className="max-w-6xl mx-auto px-6 py-20 md:py-28 text-center">
          {copy.home.badge && (
            <p
              className="text-xs md:text-sm uppercase mb-5"
              style={{ color: GOLD, letterSpacing: "0.35em", fontWeight: 500 }}
            >
              {copy.home.badge}
            </p>
          )}
          {hasTitle && (
            <h1
              className="text-4xl md:text-6xl mb-6 leading-tight"
              style={{ color: BLUE, fontWeight: 300, letterSpacing: "-0.02em" }}
            >
              {copy.home.titleLead}
              <br />
              <span style={{ fontWeight: 600 }}>{copy.home.titleEmphasis}</span>
            </h1>
          )}
          {copy.home.description && (
            <p
              className="max-w-xl mx-auto text-base md:text-lg leading-relaxed"
              style={{ color: MUTED, fontWeight: 400 }}
            >
              {copy.home.description}
            </p>
          )}
        </section>
      )}

      <section className="max-w-6xl mx-auto px-6 mb-8 pt-10">
        <div className="relative max-w-xl mx-auto">
          <Search
            className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5"
            style={{ color: GOLD }}
          />
          <input
            type="text"
            value={searchQuery}
            onChange={(event) => onSearchQueryChange(event.target.value)}
            placeholder={copy.home.searchPlaceholder}
            className="w-full pl-12 pr-5 py-3.5 rounded-full border focus:outline-none transition-colors"
            style={{
              color: BLUE,
              borderColor: BORDER,
              backgroundColor: WHITE,
              fontFamily: FONT
            }}
            onFocus={(event) => (event.currentTarget.style.borderColor = GOLD)}
            onBlur={(event) => (event.currentTarget.style.borderColor = BORDER)}
          />
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 mb-12">
        <div className="flex flex-wrap gap-2 justify-center">
          {categories.map((category) => {
            const isActive = selectedCategory === category;
            return (
              <button
                key={category}
                onClick={() => onSelectCategory(category)}
                className="px-5 py-2 rounded-full text-sm transition-all border"
                style={{
                  backgroundColor: isActive ? BLUE : WHITE,
                  color: isActive ? WHITE : BLUE,
                  borderColor: isActive ? BLUE : BORDER,
                  fontWeight: 500,
                  letterSpacing: "0.05em"
                }}
              >
                {localizeCategory(category)}
              </button>
            );
          })}
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {[...Array(6)].map((_, index) => (
              <div
                key={index}
                className="rounded-2xl overflow-hidden animate-pulse"
                style={{ backgroundColor: WHITE }}
              >
                <div className="aspect-square" style={{ backgroundColor: BG }} />
                <div className="p-6 space-y-3">
                  <div className="h-3 w-1/3 rounded" style={{ backgroundColor: BG }} />
                  <div className="h-4 w-3/4 rounded" style={{ backgroundColor: BG }} />
                  <div className="h-3 w-full rounded" style={{ backgroundColor: BG }} />
                </div>
              </div>
            ))}
          </div>
        ) : filteredDesserts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-xl" style={{ color: MUTED, fontWeight: 300 }}>
              {copy.home.noDesserts}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredDesserts.map((dessert) => (
              <div
                key={dessert.id}
                onClick={() => onOpenDetail(dessert)}
                className="group rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 hover:-translate-y-1"
                style={{
                  backgroundColor: WHITE,
                  boxShadow: "0 2px 24px rgba(9, 71, 131, 0.08)"
                }}
              >
                <div className="aspect-square overflow-hidden" style={{ backgroundColor: BG }}>
                  <img
                    src={dessert.image}
                    alt={dessert.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <p
                    className="text-xs uppercase mb-2"
                    style={{ color: GOLD, letterSpacing: "0.25em", fontWeight: 600 }}
                  >
                    {dessert.category}
                  </p>
                  <h3 className="text-lg mb-2" style={{ color: BLUE, fontWeight: 600 }}>
                    {dessert.name}
                  </h3>
                  <p
                    className="text-sm leading-relaxed mb-5 line-clamp-2"
                    style={{ color: MUTED }}
                  >
                    {dessert.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg" style={{ color: BLUE, fontWeight: 700 }}>
                      {formatPrice(dessert.price)}
                    </span>
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        onAddToCart(dessert);
                      }}
                      className="text-xs uppercase px-4 py-2 rounded-full transition-all"
                      style={{
                        backgroundColor: GOLD,
                        color: WHITE,
                        letterSpacing: "0.2em",
                        fontWeight: 600
                      }}
                      onMouseEnter={(event) =>
                        (event.currentTarget.style.backgroundColor = BLUE)
                      }
                      onMouseLeave={(event) =>
                        (event.currentTarget.style.backgroundColor = GOLD)
                      }
                    >
                      {copy.home.addButton}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

type SetsPageProps = {
  sets: DessertSet[];
  onOpenSet: (set: DessertSet) => void;
};

export function SetsPage({ sets, onOpenSet }: SetsPageProps) {
  const { copy, localizeSetSummary } = useCatalogI18n();

  return (
    <>
      <section className="max-w-6xl mx-auto px-6 py-20 md:py-24 text-center">
        <p
          className="text-xs md:text-sm uppercase mb-5"
          style={{ color: GOLD, letterSpacing: "0.35em", fontWeight: 500 }}
        >
          {copy.sets.badge}
        </p>
        <h1
          className="text-4xl md:text-6xl mb-6 leading-tight"
          style={{ color: BLUE, fontWeight: 300, letterSpacing: "-0.02em" }}
        >
          {copy.sets.titleLead} <span style={{ fontWeight: 600 }}>{copy.sets.titleEmphasis}</span>
        </h1>
        <p
          className="max-w-xl mx-auto text-base md:text-lg leading-relaxed"
          style={{ color: MUTED, fontWeight: 400 }}
        >
          {copy.sets.description}
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {sets.map((set) => {
            return (
              <div
                key={set.id}
                onClick={() => onOpenSet(set)}
                className="group rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 hover:-translate-y-1"
                style={{
                  backgroundColor: WHITE,
                  boxShadow: "0 2px 24px rgba(9, 71, 131, 0.08)"
                }}
              >
                <div
                  className="aspect-[4/3] overflow-hidden relative"
                  style={{ backgroundColor: BG }}
                >
                  <img
                    src={set.mainImage}
                    alt={set.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                  <div
                    className="absolute top-4 left-4 px-3 py-1.5 rounded-full flex items-center gap-1.5"
                    style={{ backgroundColor: "rgba(255,255,255,0.95)" }}
                  >
                    <Package className="w-3.5 h-3.5" style={{ color: GOLD }} />
                    <span
                      className="text-xs uppercase"
                      style={{ color: BLUE, fontWeight: 600, letterSpacing: "0.15em" }}
                    >
                      {localizeSetSummary(set.items.length)}
                    </span>
                  </div>
                </div>
                <div className="p-6 md:p-8">
                  {set.tagline && (
                    <p
                      className="text-xs uppercase mb-2"
                      style={{ color: GOLD, letterSpacing: "0.25em", fontWeight: 600 }}
                    >
                      {set.tagline}
                    </p>
                  )}
                  <h3
                    className="text-2xl mb-3"
                    style={{ color: BLUE, fontWeight: 700, letterSpacing: "-0.01em" }}
                  >
                    {set.name}
                  </h3>
                  <p className="text-sm leading-relaxed mb-5 line-clamp-2" style={{ color: MUTED }}>
                    {set.description}
                  </p>

                  <div className="flex -space-x-2 mb-5">
                    {set.items.slice(0, 4).map((item, index) => (
                      <div
                        key={index}
                        className="w-10 h-10 rounded-full overflow-hidden border-2"
                        style={{ borderColor: WHITE, backgroundColor: BG }}
                      >
                        <img
                          src={item.image}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xl" style={{ color: BLUE, fontWeight: 700 }}>
                      {formatPrice(set.price)}
                    </span>
                    <button
                      onClick={(event) => {
                        event.stopPropagation();
                        onOpenSet(set);
                      }}
                      className="text-xs uppercase px-5 py-2.5 rounded-full transition-all"
                      style={{
                        backgroundColor: GOLD,
                        color: WHITE,
                        letterSpacing: "0.2em",
                        fontWeight: 600
                      }}
                      onMouseEnter={(event) =>
                        (event.currentTarget.style.backgroundColor = BLUE)
                      }
                      onMouseLeave={(event) =>
                        (event.currentTarget.style.backgroundColor = GOLD)
                      }
                    >
                      {copy.sets.viewSetButton}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </>
  );
}

type PartnersPageProps = {
  partners: Partner[];
  desserts: Dessert[];
  onNavigateToContacts: () => void;
};

export function PartnersPage({ partners, desserts, onNavigateToContacts }: PartnersPageProps) {
  const { copy } = useCatalogI18n();
  const dessertById = new Map(desserts.map((dessert) => [dessert.id, dessert]));

  // Every partner branch with coordinates becomes a map marker, showing the
  // partner name and the branch website title (or address when empty).
  const mapMarkers: PartnerMapMarker[] = partners.flatMap((partner) =>
    partner.branches
      .filter((branch) => branch.coordinates != null)
      .map((branch, index) => ({
        id: `${partner.id}-${index}`,
        partnerName: partner.name,
        title: branch.title,
        coordinates: branch.coordinates as [number, number]
      }))
  );

  return (
    <>
      <section className="max-w-6xl mx-auto px-6 py-10 md:py-12 text-center">
        <p
          className="max-w-xl mx-auto text-base md:text-lg leading-relaxed"
          style={{ color: MUTED, fontWeight: 400 }}
        >
          {copy.partners.description}
        </p>
      </section>

      {mapMarkers.length > 0 && (
        <section className="max-w-6xl mx-auto px-6 pb-16">
          <div className="text-center mb-8">
            <p
              className="text-xs uppercase mb-3"
              style={{ color: GOLD, letterSpacing: "0.35em", fontWeight: 500 }}
            >
              {copy.partners.mapBadge}
            </p>
          </div>
          <div
            className="rounded-2xl overflow-hidden"
            style={{
              backgroundColor: WHITE,
              boxShadow: "0 2px 24px rgba(9, 71, 131, 0.08)"
            }}
          >
            <PartnerMap markers={mapMarkers} />
          </div>
        </section>
      )}

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {partners.map((partner) => (
            <div
              key={partner.id}
              className="rounded-2xl p-8 transition-all hover:-translate-y-1"
              style={{
                backgroundColor: WHITE,
                boxShadow: "0 2px 24px rgba(9, 71, 131, 0.08)"
              }}
            >
              <div
                className="pb-6 mb-6 border-b flex items-center gap-4"
                style={{ borderColor: BORDER }}
              >
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    backgroundColor: BG,
                    border: `2px solid ${GOLD}`
                  }}
                >
                  <span className="text-lg" style={{ color: BLUE, fontWeight: 700 }}>
                    {partner.name
                      .split(" ")
                      .map((word) => word[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase()}
                  </span>
                </div>
                <div>
                  <h3
                    className="text-2xl mb-0.5"
                    style={{ color: BLUE, fontWeight: 700, letterSpacing: "-0.01em" }}
                  >
                    {partner.name}
                  </h3>
                  {partner.tagline && (
                    <p className="text-sm" style={{ color: MUTED }}>
                      {partner.tagline}
                    </p>
                  )}
                </div>
              </div>

              <div className="mb-6">
                <p
                  className="text-xs uppercase mb-3 flex items-center gap-2"
                  style={{ color: GOLD, letterSpacing: "0.25em", fontWeight: 600 }}
                >
                  <MapPin className="w-3.5 h-3.5" />
                  {copy.partners.locationsTitle}
                </p>
                <ul className="space-y-1.5">
                  {partner.locations.map((location, index) => (
                    <li
                      key={index}
                      className="text-sm leading-relaxed pl-5 relative"
                      style={{ color: BLUE }}
                    >
                      <span
                        className="absolute left-0 top-2 w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: GOLD }}
                      />
                      {location}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <p
                  className="text-xs uppercase mb-3"
                  style={{ color: GOLD, letterSpacing: "0.25em", fontWeight: 600 }}
                >
                  {copy.partners.weSupplyTitle}
                </p>
                <div className="flex flex-wrap gap-2">
                  {partner.suppliedDessertIds.map((dessertId) => {
                    const dessert = dessertById.get(dessertId);
                    if (!dessert) {
                      return null;
                    }
                    return (
                      <span
                        key={dessertId}
                        className="text-xs px-3 py-1.5 rounded-full"
                        style={{
                          backgroundColor: BG,
                          color: BLUE,
                          fontWeight: 500
                        }}
                      >
                        {dessert.name}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className="mt-12 rounded-2xl p-10 md:p-14 text-center"
          style={{ backgroundColor: BLUE }}
        >
          <p
            className="text-xs uppercase mb-4"
            style={{ color: GOLD, letterSpacing: "0.35em", fontWeight: 500 }}
          >
            {copy.partners.ctaBadge}
          </p>
          <h2
            className="text-3xl md:text-4xl mb-4 leading-tight"
            style={{ color: WHITE, fontWeight: 300, letterSpacing: "-0.01em" }}
          >
            {copy.partners.ctaTitleLead}{" "}
            <span style={{ fontWeight: 600, color: GOLD }}>{copy.partners.ctaTitleEmphasis}</span>
          </h2>
          <p
            className="max-w-xl mx-auto mb-8"
            style={{ color: "rgba(255,255,255,0.8)", fontWeight: 400 }}
          >
            {copy.partners.ctaDescription}
          </p>
          <button
            onClick={onNavigateToContacts}
            className="px-8 py-4 rounded-full text-sm uppercase transition-colors"
            style={{
              backgroundColor: GOLD,
              color: WHITE,
              letterSpacing: "0.25em",
              fontWeight: 600
            }}
            onMouseEnter={(event) => {
              event.currentTarget.style.backgroundColor = WHITE;
              event.currentTarget.style.color = BLUE;
            }}
            onMouseLeave={(event) => {
              event.currentTarget.style.backgroundColor = GOLD;
              event.currentTarget.style.color = WHITE;
            }}
          >
            {copy.partners.ctaButton}
          </button>
        </div>
      </section>
    </>
  );
}

export function ContactsPage() {
  const { copy, localizeHoursDay } = useCatalogI18n();

  const hasHero = Boolean(
    copy.contacts.badge ||
      copy.contacts.titleLead ||
      copy.contacts.titleEmphasis ||
      copy.contacts.description
  );

  return (
    <>
      {hasHero && (
      <section className="max-w-6xl mx-auto px-6 py-20 md:py-24 text-center">
        {copy.contacts.badge && (
          <p
            className="text-xs md:text-sm uppercase mb-5"
            style={{ color: GOLD, letterSpacing: "0.35em", fontWeight: 500 }}
          >
            {copy.contacts.badge}
          </p>
        )}
        {(copy.contacts.titleLead || copy.contacts.titleEmphasis) && (
          <h1
            className="text-4xl md:text-6xl mb-6 leading-tight"
            style={{ color: BLUE, fontWeight: 300, letterSpacing: "-0.02em" }}
          >
            {copy.contacts.titleLead} <span style={{ fontWeight: 600 }}>{copy.contacts.titleEmphasis}</span>
          </h1>
        )}
        {copy.contacts.description && (
          <p
            className="max-w-xl mx-auto text-base md:text-lg leading-relaxed"
            style={{ color: MUTED, fontWeight: 400 }}
          >
            {copy.contacts.description}
          </p>
        )}
      </section>
      )}

      <section className={`max-w-6xl mx-auto px-6 pb-12 ${hasHero ? "" : "pt-12 md:pt-16"}`}>
        <div className="grid md:grid-cols-2 gap-6">
          <div
            className="rounded-2xl p-8 md:p-10"
            style={{
              backgroundColor: WHITE,
              boxShadow: "0 2px 24px rgba(9, 71, 131, 0.08)"
            }}
          >
            <h2
              className="text-2xl mb-8"
              style={{ color: BLUE, fontWeight: 700, letterSpacing: "-0.01em" }}
            >
              {copy.contacts.detailsTitle}
            </h2>

            <div className="space-y-6">
              <div className="flex gap-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: BG }}
                >
                  <MapPin className="w-4 h-4" style={{ color: GOLD }} />
                </div>
                <div>
                  <p
                    className="text-xs uppercase mb-1"
                    style={{ color: GOLD, letterSpacing: "0.25em", fontWeight: 600 }}
                  >
                    {copy.contacts.addressLabel}
                  </p>
                  <p style={{ color: BLUE, fontWeight: 500 }}>{CONTACT_INFO.address}</p>
                </div>
              </div>

              <div className="flex gap-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: BG }}
                >
                  <Phone className="w-4 h-4" style={{ color: GOLD }} />
                </div>
                <div>
                  <p
                    className="text-xs uppercase mb-1"
                    style={{ color: GOLD, letterSpacing: "0.25em", fontWeight: 600 }}
                  >
                    {copy.contacts.phoneLabel}
                  </p>
                  <a href={`tel:${CONTACT_INFO.phone}`} style={{ color: BLUE, fontWeight: 500 }}>
                    {CONTACT_INFO.phone}
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: BG }}
                >
                  <Mail className="w-4 h-4" style={{ color: GOLD }} />
                </div>
                <div>
                  <p
                    className="text-xs uppercase mb-1"
                    style={{ color: GOLD, letterSpacing: "0.25em", fontWeight: 600 }}
                  >
                    {copy.contacts.emailLabel}
                  </p>
                  <a
                    href={`mailto:${CONTACT_INFO.email}`}
                    style={{ color: BLUE, fontWeight: 500 }}
                  >
                    {CONTACT_INFO.email}
                  </a>
                </div>
              </div>

              <div className="flex gap-4">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: BG }}
                >
                  <Clock className="w-4 h-4" style={{ color: GOLD }} />
                </div>
                <div className="flex-1">
                  <p
                    className="text-xs uppercase mb-2"
                    style={{ color: GOLD, letterSpacing: "0.25em", fontWeight: 600 }}
                  >
                    {copy.contacts.hoursLabel}
                  </p>
                  <ul className="space-y-1.5">
                    {CONTACT_INFO.hours.map((hour, index) => (
                      <li
                        key={index}
                        className="flex justify-between gap-4 text-sm"
                        style={{ color: BLUE }}
                      >
                        <span style={{ fontWeight: 500 }}>{localizeHoursDay(hour.days)}</span>
                        <span style={{ color: MUTED }}>{hour.time}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div
            className="rounded-2xl overflow-hidden"
            style={{
              backgroundColor: WHITE,
              boxShadow: "0 2px 24px rgba(9, 71, 131, 0.08)",
              minHeight: "400px"
            }}
          >
            <iframe
              src={yandexMapSrc(CONTACT_INFO.coordinates)}
              title={copy.contacts.mapTitle}
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: "400px" }}
              loading="lazy"
            />
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="text-center mb-10">
          <p
            className="text-xs uppercase mb-4"
            style={{ color: GOLD, letterSpacing: "0.35em", fontWeight: 500 }}
          >
            {copy.contacts.watchBadge}
          </p>
          <h2
            className="text-3xl md:text-4xl leading-tight"
            style={{ color: BLUE, fontWeight: 300, letterSpacing: "-0.01em" }}
          >
            {copy.contacts.watchLead}{" "}
            <span style={{ fontWeight: 600 }}>{copy.contacts.watchEmphasis}</span>
          </h2>
        </div>

        <div
          className="rounded-2xl overflow-hidden relative"
          style={{
            backgroundColor: BLUE,
            boxShadow: "0 2px 24px rgba(9, 71, 131, 0.15)",
            aspectRatio: "16 / 9"
          }}
        >
          {CONTACT_INFO.youtubeId === "YOUR_VIDEO_ID" ? (
            <div
              className="absolute inset-0 flex flex-col items-center justify-center text-center p-8"
              style={{ backgroundColor: BLUE }}
            >
              <div
                className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
                style={{ backgroundColor: "rgba(255,255,255,0.1)" }}
              >
                <Play className="w-8 h-8 ml-1" style={{ color: GOLD }} fill={GOLD} />
              </div>
              <p className="text-lg md:text-xl mb-2" style={{ color: WHITE, fontWeight: 500 }}>
                {copy.contacts.videoPlaceholderTitle}
              </p>
              <p className="text-sm max-w-sm" style={{ color: "rgba(255,255,255,0.7)" }}>
                {copy.contacts.videoPlaceholderLead}
                <code
                  className="px-2 py-0.5 rounded"
                  style={{
                    backgroundColor: "rgba(255,255,255,0.1)",
                    color: GOLD,
                    fontFamily: "monospace",
                    fontSize: "0.85em"
                  }}
                >
                  youtubeId
                </code>
                {copy.contacts.videoPlaceholderTail}
              </p>
            </div>
          ) : (
            <iframe
              src={`https://www.youtube.com/embed/${CONTACT_INFO.youtubeId}`}
              title={copy.contacts.videoIframeTitle}
              width="100%"
              height="100%"
              style={{ border: 0, position: "absolute", inset: 0 }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
        </div>
      </section>
    </>
  );
}
